import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, Heart, ThumbsDown, MessageSquare, AlertTriangle } from 'lucide-react';

// Toast notification component
const Toast = ({ message, type = 'success', onClose, duration = 2500 }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const icons = {
    success: <Check className="h-4 w-4" />,
    error: <X className="h-4 w-4" />,
    like: <Heart className="h-4 w-4 fill-current" />,
    dislike: <ThumbsDown className="h-4 w-4 fill-current" />,
    comment: <MessageSquare className="h-4 w-4" />,
    warning: <AlertTriangle className="h-4 w-4" />
  };

  const colors = {
    success: 'bg-emerald-500 text-white',
    error: 'bg-red-500 text-white',
    like: 'bg-red-500 text-white',
    dislike: 'bg-blue-500 text-white',
    comment: 'bg-emerald-500 text-white',
    warning: 'bg-amber-500 text-white'
  };

  return createPortal(
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] animate-in slide-in-from-bottom-4 fade-in duration-200">
      <div className={`flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg ${colors[type]}`}>
        {icons[type]}
        <span className="text-sm font-medium">{message}</span>
      </div>
    </div>,
    document.body
  );
};

// Toast manager to handle multiple toasts
export const ToastContext = React.createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    // Return a fallback if used outside provider
    return { showToast: () => {} };
  }
  return context;
};

export default Toast;
